import { Download, Beaker, Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { SPECS } from "@/config/specs";
import { PRODUCT_IMAGES } from "@/config/productImages";
import { PRODUCT_SPECS } from "@/config/productSpecs";
import SafeImage from "./SafeImage";

const Products = () => {
  const { t } = useLanguage();
  const products = [
    {
      id: "cal-calcitica-virgem" as const,
      icon: Beaker,
      name: "CaO (Cal Virgem Calcítica)",
      description: "Cal virgem calcítica de alta pureza e reatividade para aplicações industriais exigentes.",
      applications: ["Siderurgia", "Tratamento de efluentes", "Indústria química"]
    },
    {
      id: "cal-calcitica-hidratada" as const,
      icon: Hammer,
      name: "Ca(OH)₂ (Cal Hidratada Calcítica)",
      description: "Cal hidratada calcítica para construção civil e aplicações técnicas especializadas.",
      applications: ["Construção civil", "Saneamento", "Papel e celulose"]
    },
    {
      id: "cal-dolomitica-virgem" as const,
      icon: Beaker,
      name: "MgO (Cal Virgem Dolomítica)",
      description: "Cal virgem dolomítica com alto teor de magnésio para aplicações especializadas.",
      applications: ["Refratários", "Metalurgia", "Indústria química"]
    },
    {
      id: "cal-dolomitica-hidratada" as const,
      icon: Hammer,
      name: "Mg(OH)₂ (Cal Hidratada Dolomítica)",
      description: "Cal hidratada dolomítica para aplicações que requerem propriedades especiais.",
      applications: ["Neutralização", "Tratamento", "Formulações especiais"]
    }
  ];

  return (
    <section id="produtos" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('products.title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('products.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {products.map((product, index) => (
            <Card 
              key={index}
              id={product.id}
              className="fade-in border-0 shadow-medium hover:shadow-strong transition-all duration-300 bg-card scroll-mt-24"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <CardHeader className="text-center pb-6">
                {/* Product Image */}
                <div className="mb-6">
                  <SafeImage 
                    src={PRODUCT_IMAGES[product.id]} 
                    alt={`Embalagem ${product.name}`}
                    className="w-32 h-32 mx-auto object-contain rounded-lg"
                    fallbackSrc="/media/CAL-VIRGEM.png"
                    webpSrc="/media/CAL-VIRGEM.webp"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Imagem da embalagem meramente ilustrativa.
                  </p>
                </div>
                
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4 mx-auto">
                  <product.icon className="w-8 h-8" />
                </div>
                <CardTitle className="text-2xl text-foreground">
                  {product.name}
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground">
                  {product.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Especificações Técnicas */}
                <div>
                  <h4 className="font-semibold text-foreground mb-3">
                    Especificações Químicas
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {Object.entries(PRODUCT_SPECS[product.id]?.quimica || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-1 border-b border-muted/30">
                        <span className="text-muted-foreground">{key}:</span>
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Granulometria */}
                <div>
                  <h4 className="font-semibold text-foreground mb-3">
                    Granulometria
                  </h4>
                  <div className="space-y-2 text-sm">
                    {Object.entries(PRODUCT_SPECS[product.id]?.granulometria || {}).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-medium text-foreground">{key}:</span>
                        <span className="text-muted-foreground ml-2">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Aplicações */}
                <div>
                  <h4 className="font-semibold text-foreground mb-3">
                    Principais Aplicações
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {product.applications.map((app, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
                      >
                        {app}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button 
                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                    onClick={() => window.open(SPECS[product.id as keyof typeof SPECS]?.pdf, '_blank')}
                    aria-label={`Baixar especificação ${product.name} (PDF)`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('products.download_spec')}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-primary text-primary hover:bg-primary/10"
                    onClick={() => {
                      // Create a modal or navigate to quotation form
                      const QuotationForm = () => import('@/components/QuotationForm');
                      // For now, just redirect to WhatsApp with product info
                      const message = `Olá, gostaria de solicitar uma cotação para ${product.name}. Podem me ajudar?`;
                      window.open(`https://wa.me/5537998606150?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                  >
                    {t('products.request_quote')}
                  </Button>
                </div>

                {/* Disclaimers */}
                <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t border-muted/30">
                  <p>Os valores indicados são apenas referenciais. A garantia de qualidade é estabelecida conforme a especificação definida pelo cliente e atendida de acordo com suas necessidades.</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
